import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Calendar as CalIcon, Trash2, LogIn as LogInIcon, X } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const STATUS_COLOR = {
    confirmed: "bg-indigo-100 text-indigo-800 border-indigo-200",
    checked_in: "bg-emerald-100 text-emerald-800 border-emerald-200",
    checked_out: "bg-slate-100 text-slate-700 border-slate-200",
    cancelled: "bg-red-100 text-red-800 border-red-200",
    no_show: "bg-orange-100 text-orange-800 border-orange-200",
};

const SOURCE_LABEL = {
    walk_in: "Walk-in", online: "Online", travel_agent: "Travel Agent",
    corporate: "Corporate", group: "Group",
};

const emptyForm = {
    guest_name: "", guest_email: "", guest_phone: "", guest_id_type: "Passport", guest_id_number: "",
    room_id: "", check_in_date: "", check_out_date: "", adults: 1, children: 0,
    source: "walk_in", rate: 0, discount: 0, special_requests: "",
};

export default function Reservations() {
    const { activeHotelId } = useAuth();
    const [reservations, setReservations] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [filter, setFilter] = useState("all");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);

    const load = async () => {
        if (!activeHotelId) return;
        try {
            const [r, rm] = await Promise.all([
                api.get(`/reservations?hotel_id=${activeHotelId}`),
                api.get(`/rooms?hotel_id=${activeHotelId}`),
            ]);
            setReservations(r.data);
            setRooms(rm.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => { load(); }, [activeHotelId]);

    const onSelectRoom = (rid) => {
        const room = rooms.find(r => r.id === rid);
        setForm({ ...form, room_id: rid, rate: room?.room_type?.base_rate || 0 });
    };

    const submit = async () => {
        try {
            await api.post("/reservations", {
                ...form, hotel_id: activeHotelId,
                adults: Number(form.adults), children: Number(form.children),
                rate: Number(form.rate), discount: Number(form.discount),
            });
            toast.success("Reservation created");
            setOpen(false);
            setForm(emptyForm);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const cancel = async (id) => {
        if (!confirm("Cancel this reservation?")) return;
        try { await api.post(`/reservations/${id}/cancel`); toast.success("Cancelled"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const checkIn = async (id) => {
        try { await api.post(`/reservations/${id}/checkin`, { id_verified: true, deposit: 0, key_card_issued: true }); toast.success("Checked in"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const filtered = filter === "all" ? reservations : reservations.filter(r => r.status === filter);
    const availableRooms = rooms.filter(r => ["available", "dirty", "cleaning"].includes(r.status));

    const today = new Date().toISOString().slice(0, 10);
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

    return (
        <AppShell title="Reservations" subtitle={`${reservations.length} bookings`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div className="flex gap-2">
                    {["all", "confirmed", "checked_in", "checked_out", "cancelled"].map(s => (
                        <button key={s} onClick={() => setFilter(s)}
                            className={`px-3 py-1.5 text-xs rounded-sm border ${filter === s ? "bg-[#0A1C3E] text-white border-[#0A1C3E]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                            data-testid={`res-filter-${s}`}>
                            {s === "all" ? "All" : s.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}
                        </button>
                    ))}
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[#0A1C3E] hover:bg-[#132D5C] rounded-sm" data-testid="new-reservation-btn">
                            <Plus className="w-4 h-4 mr-1" /> New Booking
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader><DialogTitle>New Reservation</DialogTitle></DialogHeader>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs uppercase tracking-wider">Guest name</Label>
                                <Input value={form.guest_name} onChange={e => setForm({ ...form, guest_name: e.target.value })} data-testid="res-guest-name" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Phone</Label>
                                <Input value={form.guest_phone} onChange={e => setForm({ ...form, guest_phone: e.target.value })} data-testid="res-guest-phone" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Email</Label>
                                <Input value={form.guest_email} onChange={e => setForm({ ...form, guest_email: e.target.value })} /></div>
                            <div><Label className="text-xs uppercase tracking-wider">ID Number</Label>
                                <Input value={form.guest_id_number} onChange={e => setForm({ ...form, guest_id_number: e.target.value })} /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Check-in date</Label>
                                <Input type="date" value={form.check_in_date || today} onChange={e => setForm({ ...form, check_in_date: e.target.value })} data-testid="res-checkin-date" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Check-out date</Label>
                                <Input type="date" value={form.check_out_date || tomorrow} onChange={e => setForm({ ...form, check_out_date: e.target.value })} data-testid="res-checkout-date" /></div>
                            <div className="col-span-2"><Label className="text-xs uppercase tracking-wider">Room</Label>
                                <Select value={form.room_id} onValueChange={onSelectRoom}>
                                    <SelectTrigger data-testid="res-room-select"><SelectValue placeholder="Choose an available room" /></SelectTrigger>
                                    <SelectContent>
                                        {availableRooms.map(r => (
                                            <SelectItem key={r.id} value={r.id}>
                                                {r.number} · {r.room_type?.name} · ₹{r.room_type?.base_rate}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div><Label className="text-xs uppercase tracking-wider">Adults</Label>
                                <Input type="number" min="1" value={form.adults} onChange={e => setForm({ ...form, adults: e.target.value })} /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Children</Label>
                                <Input type="number" min="0" value={form.children} onChange={e => setForm({ ...form, children: e.target.value })} /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Rate / night</Label>
                                <Input type="number" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} data-testid="res-rate" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Discount</Label>
                                <Input type="number" value={form.discount} onChange={e => setForm({ ...form, discount: e.target.value })} /></div>
                            <div className="col-span-2"><Label className="text-xs uppercase tracking-wider">Source</Label>
                                <Select value={form.source} onValueChange={v => setForm({ ...form, source: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(SOURCE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={submit} disabled={!form.guest_name || !form.room_id} className="bg-[#0A1C3E]" data-testid="res-submit">Create Booking</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                            <th className="px-4 py-3">Guest</th>
                            <th className="px-4 py-3">Room</th>
                            <th className="px-4 py-3">Check-in</th>
                            <th className="px-4 py-3">Check-out</th>
                            <th className="px-4 py-3">Nights</th>
                            <th className="px-4 py-3">Total</th>
                            <th className="px-4 py-3">Balance</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(r => {
                            const room = rooms.find(rm => rm.id === r.room_id);
                            return (
                                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`res-row-${r.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="text-sm font-medium text-slate-900">{r.guest_name}</div>
                                        <div className="text-xs text-slate-500">{r.guest_phone || r.guest_email}</div>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-sm">{room?.number || "—"}</td>
                                    <td className="px-4 py-3 text-sm">{r.check_in_date?.slice(0, 10)}</td>
                                    <td className="px-4 py-3 text-sm">{r.check_out_date?.slice(0, 10)}</td>
                                    <td className="px-4 py-3 text-sm font-mono">{r.nights}</td>
                                    <td className="px-4 py-3 text-sm font-mono font-semibold">₹{Number(r.total || 0).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-sm font-mono">₹{Number(r.balance || 0).toLocaleString()}</td>
                                    <td className="px-4 py-3">
                                        <span className={`status-badge ${STATUS_COLOR[r.status]}`}>{r.status?.replace(/_/g, " ")}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-1">
                                            {r.status === "confirmed" && (
                                                <Button size="sm" variant="outline" onClick={() => checkIn(r.id)} className="h-7 text-xs" data-testid={`checkin-${r.id}`}>
                                                    <LogInIcon className="w-3 h-3 mr-1" /> Check-in
                                                </Button>
                                            )}
                                            <Link to={`/checkin?res=${r.id}`}>
                                                <Button size="sm" variant="ghost" className="h-7 text-xs">View</Button>
                                            </Link>
                                            {!["cancelled", "checked_out"].includes(r.status) && (
                                                <button onClick={() => cancel(r.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`cancel-${r.id}`}>
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr><td colSpan="9" className="text-center py-10 text-slate-500 text-sm">No reservations yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </Card>
        </AppShell>
    );
}
